<template>
  <div>
    <div v-if="$slots.label" class="mb-2 text-sm font-medium">
      <slot name="label" />
    </div>
    <div
      :class="twMerge('flex flex-wrap items-center', wrapperClasses as string)"
    >
      <button
        v-for="option in options"
        :key="option.value.toString()"
        :test_id="option.label"
        :class="
          twMerge(
            'm-1 flex h-full cursor-pointer items-center justify-between space-x-2 rounded-2xl border-2 p-4 shadow-sm focus:outline-none focus-visible:ring focus-visible:ring-neutral-200 focus-visible:ring-offset-2',
            themeColors[themeColor].hover,
            isSelected(option)
              ? themeColors[themeColor].selected
              : themeColors[themeColor].unselected,
            buttonClasses,
          )
        "
        @click="setSelectedOption(option)"
      >
        <span>
          <slot :option="option" />
        </span>
        <CheckIcon
          v-if="!hideCheckIcon"
          class="order-1 ml-2 size-4 shrink-0"
          :class="[
            themeColors[themeColor].checkIcon,
            { invisible: !isSelected(option) },
          ]"
        />
      </button>
    </div>
    <FormErrors v-if="hasErrors" :errors="errors" class="mt-2" />
  </div>
</template>

<script setup lang="ts" generic="T">
import FormErrors from './FormErrors.vue'
import CheckIcon from '~icons/lucide/check'
import { computed } from 'vue'
import { twMerge } from 'tailwind-merge'
import type { FormOptionType } from '@/shared/types'

const props = withDefaults(
  defineProps<{
    options: (T extends FormOptionType ? T : never)[]
    modelValue?: string | number | null
    errors?: string[] | string
    buttonClasses?: string | string[]
    wrapperClasses?: string | string[]
    hideCheckIcon?: boolean
    themeColor?: 'primary' | 'secondary'
  }>(),
  {
    modelValue: null,
    errors: '',
    buttonClasses: '',
    wrapperClasses: '',
    themeColor: 'secondary',
    hideCheckIcon: false,
  },
)

const emit = defineEmits(['update:modelValue', 'input'])

const hasErrors = computed(() => {
  const isString = typeof props.errors === 'string'
  return isString ? props.errors : props.errors.length
})

const isSelected = (option: FormOptionType) => option.value === props.modelValue

const setSelectedOption = (option: FormOptionType) => {
  if (isSelected(option)) return
  emit('update:modelValue', option.value)
  // 'input' emit is backward compatibility with Vue 2 v-model
  emit('input', option.value)
}

const themeColors = {
  primary: {
    selected: 'border-primary-500 hover:border-primary-500',
    unselected: 'border-neutral-50',
    hover: 'hover:border-primary-200',
    checkIcon: 'text-primary-500',
  },
  secondary: {
    selected: 'border-secondary-500 hover:border-secondary-500',
    unselected: 'border-secondary-50',
    hover: 'hover:border-secondary-200',
    checkIcon: 'text-secondary-500',
  },
}
</script>
